/**
 * ScenarioCompareModal - Side-by-side comparison of multiple scenarios
 */
import React, { useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Typography,
    Tabs,
    Tab,
    IconButton,
    alpha
} from '@mui/material';
import { Close as CloseIcon, TableChart, GridOn, PictureAsPdf, Dashboard as DashboardIcon } from '@mui/icons-material';
import { vortxColors, retroColors, useThemeMode, exportInvestmentModel, exportFullData, exportPresentation, vortxTypography } from '@vortx/shared-ui';

// Scenario colors for consistent identification
const SCENARIO_COLORS = [
    vortxColors.gold,
    vortxColors.teal,
    '#6B8E23', // Olive
    '#8B4513', // Saddle brown
    '#4682B4', // Steel blue
];

function ComparisonTable({ rows, scenarios, bestScenarioIndex = null, isRetro = false }) {
    return (
        <Box sx={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                    <tr>
                        <th style={{
                            textAlign: 'left',
                            padding: '12px 16px',
                            borderBottom: `2px solid ${vortxColors.gold}`,
                            fontFamily: isRetro ? vortxTypography.mono : vortxTypography.body,
                            fontWeight: isRetro ? 600 : 700,
                            fontSize: isRetro ? '0.75rem' : undefined,
                            textTransform: isRetro ? 'uppercase' : undefined,
                            letterSpacing: isRetro ? '0.1em' : undefined,
                            color: isRetro ? vortxColors.gold : undefined,
                        }}>
                            Metric
                        </th>
                        {scenarios.map((s, idx) => (
                            <th key={s.id} style={{
                                textAlign: 'right',
                                padding: '12px 16px',
                                borderBottom: `2px solid ${SCENARIO_COLORS[idx]}`,
                                fontFamily: isRetro ? vortxTypography.mono : vortxTypography.body,
                                fontWeight: isRetro ? 600 : 700,
                                fontSize: isRetro ? '0.75rem' : undefined,
                                textTransform: isRetro ? 'uppercase' : undefined,
                                letterSpacing: isRetro ? '0.1em' : undefined,
                                color: SCENARIO_COLORS[idx]
                            }}>
                                {s.name}{bestScenarioIndex === idx && ' ✓'}
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {rows.map((row, rowIdx) => {
                        const values = scenarios.map(s => row.getValue(s));

                        return (
                            <tr key={row.label} style={{
                                backgroundColor: rowIdx % 2 === 0 ? 'transparent' : (isRetro ? 'rgba(95, 212, 196, 0.05)' : alpha(vortxColors.warmStone, 0.1))
                            }}>
                                <td style={{
                                    padding: '12px 16px',
                                    fontFamily: isRetro ? vortxTypography.mono : vortxTypography.body,
                                    fontWeight: 500,
                                    fontSize: isRetro ? '0.8rem' : undefined,
                                    color: isRetro ? vortxColors.gray : undefined,
                                }}>
                                    {row.label}
                                </td>
                                {values.map((value, idx) => {
                                    const isNegative = typeof value === 'number' && value < 0;

                                    return (
                                        <td key={idx} style={{
                                            textAlign: 'right',
                                            padding: '12px 16px',
                                            fontFamily: isRetro ? vortxTypography.mono : vortxTypography.body,
                                            fontWeight: 400,
                                            fontSize: isRetro ? '0.8rem' : undefined,
                                            color: isNegative ? vortxColors.error : (isRetro ? vortxColors.gray : 'inherit')
                                        }}>
                                            {row.format ? row.format(value) : value}
                                        </td>
                                    );
                                })}
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </Box>
    );
}

// Data extraction helpers

// Get the most recent optimization run for a scenario
function getLatestRun(scenario) {
    const runs = scenario.optimization_runs || [];
    if (runs.length === 0) return null;

    // If we have last_run_uuid, find that specific run
    const lastRunUuid = scenario.last_run_uuid;
    if (lastRunUuid) {
        const matchingRun = runs.find(r => r.id === lastRunUuid || r.nrel_run_uuid === lastRunUuid);
        if (matchingRun) return matchingRun;
    }

    // Fall back to most recent completed run
    const completedRuns = runs.filter(r => ['completed', 'optimal', 'Optimal Obtained'].includes(r.status));
    if (completedRuns.length > 0) {
        // Sort by completed_at descending
        return completedRuns.sort((a, b) => {
            const aTime = a.completed_at ? new Date(a.completed_at) : new Date(0);
            const bTime = b.completed_at ? new Date(b.completed_at) : new Date(0);
            return bTime - aTime;
        })[0];
    }

    return runs[0]; // Last resort: return first run
}

// Get outputs from last_run_results (primary) or reopt_results (fallback)
function getOutputs(scenario) {
    const results = scenario.last_run_results || scenario.reopt_results || {};
    const outputs = results.outputs || {};
    // Handle nested structure (outputs.Scenario.Site.*) or flat structure (outputs.*)
    return outputs.Scenario?.Site || outputs;
}

function getSystemConfig(scenario) {
    const existingSolarKw = parseFloat(scenario.ui_state?.loadProfile?.existingSolarParams?.system_capacity) || 0;

    // Priority 1: Get data from optimization_runs (new storage pattern)
    const latestRun = getLatestRun(scenario);
    if (latestRun) {
        return {
            pvKw: latestRun.pv_kw ?? 0,
            existingSolarKw,
            storageKw: latestRun.storage_kw ?? 0,
            storageKwh: latestRun.storage_kwh ?? 0,
            generatorKw: latestRun.generator_kw ?? 0,
            tariff: scenario.tariff_display?.name || scenario.tariff_data?.name || scenario.tariff_label || 'N/A',
        };
    }

    // Priority 2: Fall back to last_run_results.outputs (legacy pattern)
    const outputs = getOutputs(scenario);

    // Check if PV was enabled in the form inputs
    // If usePv is false, there's no new solar regardless of outputs
    const usePv = scenario.ui_state?.form?.solar?.usePv ?? false;
    const totalOptimizedPvKw = outputs.PV?.size_kw ?? 0;

    // New solar = total optimized - existing (REopt returns total including existing)
    // But only if PV was actually enabled in inputs
    let newSolarKw = 0;
    if (usePv && totalOptimizedPvKw > existingSolarKw) {
        newSolarKw = totalOptimizedPvKw - existingSolarKw;
    }

    return {
        pvKw: newSolarKw,
        existingSolarKw,
        storageKw: outputs.ElectricStorage?.size_kw ?? 0,
        storageKwh: outputs.ElectricStorage?.size_kwh ?? 0,
        generatorKw: (outputs.Generator?.size_kw ?? 0) + (outputs.CHP?.size_kw ?? 0),
        tariff: scenario.tariff_display?.name || scenario.tariff_data?.name || scenario.tariff_label || 'N/A',
    };
}

function formatCurrency(value) {
    if (value === null || value === undefined) return '-';
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(value);
}

function formatNumber(value, decimals = 1) {
    if (value === null || value === undefined) return '-';
    return value.toLocaleString(undefined, { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
}

function formatPayback(value) {
    if (value === null || value === undefined) return '-';
    // Negative or zero payback means immediate return (no capital outlay or instant payback)
    if (value <= 0) return 'Immediate';
    return `${value.toFixed(1)} years`;
}

function getCashFlows(scenario) {
    // Priority 1: Get from ui_state.financialSummary (post-processed data saved by ScenarioEditor)
    const savedCashFlows = scenario.ui_state?.financialSummary?.cash_flows;
    if (savedCashFlows && savedCashFlows.length > 0) {
        return savedCashFlows;
    }

    // Priority 2: Check optimization_runs for calculated_financials
    const latestRun = getLatestRun(scenario);
    if (latestRun?.calculated_financials?.cash_flows) {
        return latestRun.calculated_financials.cash_flows;
    }

    // Priority 3: Fall back to solver outputs (legacy)
    const outputs = getOutputs(scenario);
    const financial = outputs.Financial || {};
    return financial.cashflows || [];
}

function getCapexBreakdown(scenario) {
    // Priority 1: Get from ui_state.financialSummary (post-processed data saved by ScenarioEditor)
    const savedCapex = scenario.ui_state?.financialSummary?.capex_breakdown;
    if (savedCapex) {
        return {
            solar: savedCapex.solar ?? null,
            storage: savedCapex.storage ?? null,
            generator: savedCapex.generator ?? null,
            bos: savedCapex.bos ?? null,
            installation: null,
            total: savedCapex.total ?? null,
        };
    }

    // Priority 2: Check optimization_runs for calculated_financials
    const latestRun = getLatestRun(scenario);
    if (latestRun?.calculated_financials?.capex_breakdown) {
        const capex = latestRun.calculated_financials.capex_breakdown;
        return {
            solar: capex.solar ?? null,
            storage: capex.storage ?? null,
            generator: capex.generator ?? null,
            bos: capex.bos ?? null,
            installation: null,
            total: capex.total ?? null,
        };
    }

    // Priority 3: No solver fallback - return nulls to indicate missing budget data
    // Solver financial outputs should NEVER be used for CapEx display
    return {
        solar: null,
        storage: null,
        generator: null,
        bos: null,
        installation: null,
        total: null,
        _budgetDataMissing: true, // Flag for UI to show warning
    };
}

function getPerformanceData(scenario) {
    const outputs = getOutputs(scenario);

    // Check if PV was actually enabled - don't report stale production data
    const usePv = scenario.ui_state?.form?.solar?.usePv ?? false;
    const existingSolarKw = parseFloat(scenario.ui_state?.loadProfile?.existingSolarParams?.system_capacity) || 0;
    const hasPv = usePv || existingSolarKw > 0;

    const pvProduction = hasPv ? (outputs.PV?.year_one_energy_produced_kwh ?? 0) : 0;
    const loadKwh = outputs.ElectricLoad?.annual_calculated_kwh ?? 0;
    const gridKwh = outputs.ElectricUtility?.annual_energy_supplied_kwh ?? loadKwh;

    return {
        pvProduction,
        loadKwh,
        gridKwh,
        gridReduction: loadKwh > 0 ? ((loadKwh - gridKwh) / loadKwh * 100) : 0,
        selfConsumption: pvProduction > 0 ? Math.min(100, (loadKwh - gridKwh) / pvProduction * 100) : 0,
    };
}

function TabPanel({ children, value, index, ...other }) {
    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`compare-tabpanel-${index}`}
            aria-labelledby={`compare-tab-${index}`}
            {...other}
        >
            {value === index && <Box sx={{ py: 2 }}>{children}</Box>}
        </div>
    );
}

export default function ScenarioCompareModal({ open, onClose, scenarios, projectName, onToggleView }) {
    const [activeTab, setActiveTab] = useState(0);
    const { isRetro } = useThemeMode();

    // Helper to get financial data - checks ui_state.financialSummary first, then optimization_runs
    const getFinancialSummary = (scenario) => {
        // Priority 1: Get from ui_state.financialSummary (post-processed data saved by ScenarioEditor)
        const savedSummary = scenario.ui_state?.financialSummary;
        if (savedSummary && (savedSummary.npv !== undefined || savedSummary.capex_breakdown)) {
            return {
                npv: savedSummary.npv ?? null,
                paybackPeriod: savedSummary.paybackPeriod ?? null,
                totalCapex: savedSummary.capex_breakdown?.total ?? savedSummary.initialInvestment ?? null,
                totalSavings: savedSummary.totalSavings ?? null,
                annualSavings: savedSummary.annualSavings ?? null,
            };
        }

        // Priority 2: Get data from optimization_runs (new storage pattern)
        const latestRun = getLatestRun(scenario);
        if (latestRun) {
            // Check for calculated_financials on the run
            const calcFin = latestRun.calculated_financials;
            return {
                npv: calcFin?.financial_summary?.npv ?? latestRun.npv ?? null,
                paybackPeriod: calcFin?.financial_summary?.payback_period ?? latestRun.simple_payback_years ?? null,
                totalCapex: calcFin?.capex_breakdown?.total ?? null,
                totalSavings: calcFin?.financial_summary?.total_savings ?? latestRun.lifecycle_savings ?? null,
                annualSavings: calcFin?.financial_summary?.annual_savings ?? latestRun.year_one_savings ?? null,
            };
        }

        // Priority 3: No solver fallback - return nulls to indicate missing budget data
        // Solver financial outputs should NEVER be used for NPV/CapEx display
        return {
            npv: null,
            paybackPeriod: null,
            totalCapex: null,
            totalSavings: null,
            annualSavings: null,
            _budgetDataMissing: true, // Flag for UI to show warning
        };
    };

    if (!scenarios || scenarios.length === 0) return null;

    const handleExportInvestment = () => {
        try {
            exportInvestmentModel(scenarios, projectName || 'Project');
        } catch (error) {
            console.error('Export failed:', error);
        }
    };

    const handleExportFullData = () => {
        try {
            exportFullData(scenarios, projectName || 'Project');
        } catch (error) {
            console.error('Export failed:', error);
        }
    };

    const handleExportPresentation = async () => {
        try {
            await exportPresentation(scenarios, projectName || 'Project');
        } catch (error) {
            console.error('PDF export failed:', error);
        }
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth={false}
            fullWidth
            PaperProps={{
                sx: {
                    width: '90vw',
                    height: '85vh',
                    maxWidth: 'none',
                    bgcolor: isRetro ? '#1a1a1a' : 'background.paper',
                    borderRadius: isRetro ? 0 : 2,
                    border: isRetro ? '1px solid rgba(63, 106, 100, 0.4)' : 'none',
                }
            }}
        >
            <DialogTitle sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                borderBottom: isRetro ? '1px solid rgba(63, 106, 100, 0.3)' : `1px solid ${alpha(vortxColors.gold, 0.3)}`,
                fontFamily: vortxTypography.heading,
                fontWeight: 700,
                color: vortxColors.gold,
                textTransform: isRetro ? 'uppercase' : undefined,
                letterSpacing: isRetro ? '0.05em' : undefined,
                textShadow: isRetro ? '0 0 15px rgba(178, 140, 80, 0.3)' : undefined,
            }}>
                Scenario Comparison
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {onToggleView && (
                        <Button
                            size="small"
                            startIcon={<DashboardIcon />}
                            onClick={onToggleView}
                            sx={isRetro ? {
                                fontFamily: vortxTypography.mono,
                                fontSize: '0.7rem',
                                textTransform: 'uppercase',
                                letterSpacing: '0.05em',
                                borderRadius: 0,
                                color: retroColors.accent,
                                border: '1px solid rgba(95, 212, 196, 0.3)',
                                '&:hover': {
                                    bgcolor: 'rgba(95, 212, 196, 0.1)',
                                    borderColor: retroColors.accent,
                                },
                            } : {
                                fontSize: '0.75rem',
                                textTransform: 'none',
                                color: vortxColors.teal,
                                border: `1px solid ${alpha(vortxColors.teal, 0.3)}`,
                                '&:hover': {
                                    bgcolor: alpha(vortxColors.teal, 0.1),
                                },
                            }}
                        >
                            Dashboard View
                        </Button>
                    )}
                    <IconButton onClick={onClose} size="small" sx={isRetro ? { color: retroColors.muted, borderRadius: 0 } : {}}>
                        <CloseIcon />
                    </IconButton>
                </Box>
            </DialogTitle>

            <DialogContent sx={{ p: 0, display: 'flex', flexDirection: 'column', bgcolor: isRetro ? 'transparent' : undefined }}>
                <Tabs
                    value={activeTab}
                    onChange={(e, newValue) => setActiveTab(newValue)}
                    sx={{
                        borderBottom: isRetro ? '1px solid rgba(63, 106, 100, 0.3)' : `1px solid ${alpha(vortxColors.gray, 0.2)}`,
                        px: 3,
                        '& .MuiTab-root': {
                            fontFamily: isRetro ? vortxTypography.mono : vortxTypography.body,
                            fontWeight: 600,
                            textTransform: isRetro ? 'uppercase' : 'none',
                            fontSize: isRetro ? '0.75rem' : undefined,
                            letterSpacing: isRetro ? '0.1em' : undefined,
                            color: isRetro ? retroColors.muted : undefined,
                        },
                        '& .Mui-selected': { color: vortxColors.gold },
                        '& .MuiTabs-indicator': { bgcolor: vortxColors.gold }
                    }}
                >
                    <Tab id="compare-tab-0" label="Overview" />
                    <Tab id="compare-tab-1" label="System Configuration" />
                    <Tab id="compare-tab-2" label="Financials" />
                    <Tab id="compare-tab-3" label="Performance" />
                </Tabs>

                <Box sx={{ flex: 1, overflow: 'auto', px: 3 }}>
                    <TabPanel value={activeTab} index={0}>
                        <Typography variant="h6" sx={{
                            mb: 3,
                            fontFamily: vortxTypography.heading,
                            color: isRetro ? vortxColors.gold : undefined,
                            textShadow: isRetro ? '0 0 10px rgba(178, 140, 80, 0.3)' : undefined,
                        }}>
                            Key Metrics Comparison
                        </Typography>
                        <ComparisonTable
                            scenarios={scenarios}
                            isRetro={isRetro}
                            bestScenarioIndex={(() => {
                                const npvValues = scenarios.map(s => getFinancialSummary(s).npv);
                                const validNpvs = npvValues.filter(v => v !== null);
                                if (validNpvs.length === 0) return null;
                                const maxNpv = Math.max(...validNpvs);
                                return npvValues.indexOf(maxNpv);
                            })()}
                            rows={[
                                {
                                    label: 'Initial Investment',
                                    getValue: (s) => getFinancialSummary(s).totalCapex,
                                    format: formatCurrency
                                },
                                {
                                    label: 'NPV (20 years)',
                                    getValue: (s) => getFinancialSummary(s).npv,
                                    format: formatCurrency
                                },
                                {
                                    label: 'Payback Period',
                                    getValue: (s) => getFinancialSummary(s).paybackPeriod,
                                    format: formatPayback
                                },
                                {
                                    label: 'Total Savings',
                                    getValue: (s) => getFinancialSummary(s).totalSavings,
                                    format: formatCurrency
                                },
                                {
                                    label: 'Annual Savings (Year 1)',
                                    getValue: (s) => getFinancialSummary(s).annualSavings,
                                    format: formatCurrency
                                },
                            ]}
                        />
                        {scenarios.some(s => !s.ui_state?.financialSummary) && (
                            <Typography variant="caption" sx={{
                                mt: 2,
                                display: 'block',
                                color: isRetro ? retroColors.muted : 'text.secondary',
                                fontFamily: isRetro ? vortxTypography.mono : undefined,
                            }}>
                                Note: Some scenarios need to be re-saved in ScenarioEditor to display financial metrics.
                            </Typography>
                        )}
                    </TabPanel>
                    <TabPanel value={activeTab} index={1}>
                        <Typography variant="h6" sx={{
                            mb: 3,
                            fontFamily: vortxTypography.heading,
                            color: isRetro ? vortxColors.gold : undefined,
                            textShadow: isRetro ? '0 0 10px rgba(178, 140, 80, 0.3)' : undefined,
                        }}>
                            System Configuration
                        </Typography>
                        <ComparisonTable
                            scenarios={scenarios}
                            isRetro={isRetro}
                            rows={[
                                {
                                    label: 'Existing Solar (kW)',
                                    getValue: (s) => getSystemConfig(s).existingSolarKw,
                                    format: (v) => v > 0 ? formatNumber(v, 0) : '-'
                                },
                                {
                                    label: 'New Solar (kW)',
                                    getValue: (s) => getSystemConfig(s).pvKw,
                                    format: (v) => v > 0 ? formatNumber(v, 0) : '-'
                                },
                                {
                                    label: 'Battery Power (kW)',
                                    getValue: (s) => getSystemConfig(s).storageKw,
                                    format: (v) => v > 0 ? formatNumber(v, 0) : '-'
                                },
                                {
                                    label: 'Battery Capacity (kWh)',
                                    getValue: (s) => getSystemConfig(s).storageKwh,
                                    format: (v) => v > 0 ? formatNumber(v, 0) : '-'
                                },
                                {
                                    label: 'Generator (kW)',
                                    getValue: (s) => getSystemConfig(s).generatorKw,
                                    format: (v) => v > 0 ? formatNumber(v, 0) : '-'
                                },
                                {
                                    label: 'Tariff',
                                    getValue: (s) => getSystemConfig(s).tariff,
                                    format: (v) => v
                                },
                            ]}
                        />
                    </TabPanel>
                    <TabPanel value={activeTab} index={2}>
                        <Typography variant="h6" sx={{
                            mb: 3,
                            fontFamily: vortxTypography.heading,
                            color: isRetro ? vortxColors.gold : undefined,
                            textShadow: isRetro ? '0 0 10px rgba(178, 140, 80, 0.3)' : undefined,
                        }}>
                            CapEx Breakdown
                        </Typography>
                        <ComparisonTable
                            scenarios={scenarios}
                            isRetro={isRetro}
                            rows={[
                                {
                                    label: 'Solar PV',
                                    getValue: (s) => getCapexBreakdown(s).solar,
                                    format: formatCurrency
                                },
                                {
                                    label: 'Battery Storage',
                                    getValue: (s) => getCapexBreakdown(s).storage,
                                    format: formatCurrency
                                },
                                {
                                    label: 'Generator',
                                    getValue: (s) => getCapexBreakdown(s).generator,
                                    format: (v) => v ? formatCurrency(v) : '-'
                                },
                                {
                                    label: 'Balance of System',
                                    getValue: (s) => getCapexBreakdown(s).bos,
                                    format: (v) => v ? formatCurrency(v) : '-'
                                },
                                {
                                    label: 'Total CapEx',
                                    getValue: (s) => getCapexBreakdown(s).total,
                                    format: formatCurrency
                                },
                            ]}
                        />

                        <Typography variant="h6" sx={{
                            mt: 4,
                            mb: 3,
                            fontFamily: vortxTypography.heading,
                            color: isRetro ? vortxColors.gold : undefined,
                            textShadow: isRetro ? '0 0 10px rgba(178, 140, 80, 0.3)' : undefined,
                        }}>
                            Cash Flows (First 10 Years)
                        </Typography>
                        <Box sx={{ overflowX: 'auto' }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                <thead>
                                    <tr>
                                        <th style={{
                                            textAlign: 'left',
                                            padding: '8px 12px',
                                            borderBottom: `2px solid ${vortxColors.gold}`,
                                            fontFamily: isRetro ? vortxTypography.mono : undefined,
                                            fontSize: isRetro ? '0.75rem' : undefined,
                                            textTransform: isRetro ? 'uppercase' : undefined,
                                            letterSpacing: isRetro ? '0.1em' : undefined,
                                            color: isRetro ? vortxColors.gold : undefined,
                                        }}>
                                            Year
                                        </th>
                                        {scenarios.map((s, idx) => (
                                            <th key={s.id} style={{
                                                textAlign: 'right',
                                                padding: '8px 12px',
                                                borderBottom: `2px solid ${SCENARIO_COLORS[idx]}`,
                                                color: SCENARIO_COLORS[idx],
                                                fontFamily: isRetro ? vortxTypography.mono : undefined,
                                                fontSize: isRetro ? '0.75rem' : undefined,
                                                textTransform: isRetro ? 'uppercase' : undefined,
                                                letterSpacing: isRetro ? '0.1em' : undefined,
                                            }}>
                                                {s.name}
                                            </th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {Array.from({ length: 10 }, (_, i) => i).map(year => (
                                        <tr key={year} style={{ backgroundColor: year % 2 === 0 ? 'transparent' : (isRetro ? 'rgba(95, 212, 196, 0.05)' : alpha(vortxColors.warmStone, 0.1)) }}>
                                            <td style={{
                                                padding: '8px 12px',
                                                fontFamily: isRetro ? vortxTypography.mono : undefined,
                                                fontSize: isRetro ? '0.8rem' : undefined,
                                                color: isRetro ? vortxColors.gray : undefined,
                                            }}>{year}</td>
                                            {scenarios.map(s => {
                                                const flows = getCashFlows(s);
                                                const flow = flows.find(f => f.year === year);
                                                return (
                                                    <td key={s.id} style={{
                                                        textAlign: 'right',
                                                        padding: '8px 12px',
                                                        fontFamily: isRetro ? vortxTypography.mono : undefined,
                                                        fontSize: isRetro ? '0.8rem' : undefined,
                                                        color: isRetro ? vortxColors.gray : undefined,
                                                    }}>
                                                        {flow ? formatCurrency(flow.netCashflow ?? flow.net_cashflow) : '-'}
                                                    </td>
                                                );
                                            })}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </Box>
                    </TabPanel>
                    <TabPanel value={activeTab} index={3}>
                        <Typography variant="h6" sx={{
                            mb: 3,
                            fontFamily: vortxTypography.heading,
                            color: isRetro ? vortxColors.gold : undefined,
                            textShadow: isRetro ? '0 0 10px rgba(178, 140, 80, 0.3)' : undefined,
                        }}>
                            Performance Metrics
                        </Typography>
                        <ComparisonTable
                            scenarios={scenarios}
                            isRetro={isRetro}
                            rows={[
                                {
                                    label: 'Annual PV Production (kWh)',
                                    getValue: (s) => getPerformanceData(s).pvProduction,
                                    format: (v) => formatNumber(v, 0)
                                },
                                {
                                    label: 'Annual Load (kWh)',
                                    getValue: (s) => getPerformanceData(s).loadKwh,
                                    format: (v) => formatNumber(v, 0)
                                },
                                {
                                    label: 'Grid Consumption (kWh)',
                                    getValue: (s) => getPerformanceData(s).gridKwh,
                                    format: (v) => formatNumber(v, 0)
                                },
                                {
                                    label: 'Grid Reduction',
                                    getValue: (s) => getPerformanceData(s).gridReduction,
                                    format: (v) => `${formatNumber(v, 1)}%`
                                },
                                {
                                    label: 'Self-Consumption Rate',
                                    getValue: (s) => getPerformanceData(s).selfConsumption,
                                    format: (v) => `${formatNumber(v, 1)}%`
                                },
                            ]}
                        />
                    </TabPanel>
                </Box>
            </DialogContent>

            <DialogActions sx={{
                borderTop: isRetro ? '1px solid rgba(63, 106, 100, 0.3)' : `1px solid ${alpha(vortxColors.gray, 0.2)}`,
                px: 3,
                py: 2,
                justifyContent: 'flex-start',
                gap: 2
            }}>
                <Typography variant="subtitle2" sx={{
                    color: isRetro ? retroColors.muted : 'text.secondary',
                    mr: 2,
                    fontFamily: isRetro ? vortxTypography.mono : undefined,
                    fontSize: isRetro ? '0.75rem' : undefined,
                    textTransform: isRetro ? 'uppercase' : undefined,
                }}>
                    Export:
                </Typography>
                <Button
                    variant="outlined"
                    startIcon={<TableChart />}
                    onClick={handleExportInvestment}
                    sx={isRetro ? {
                        fontFamily: vortxTypography.mono,
                        fontSize: '0.75rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.1em',
                        borderRadius: 0,
                        borderColor: 'rgba(63, 106, 100, 0.4)',
                        color: retroColors.accent,
                        '&:hover': {
                            borderColor: retroColors.accent,
                            backgroundColor: 'rgba(95, 212, 196, 0.1)',
                        },
                    } : { textTransform: 'none' }}
                >
                    Investment Model
                </Button>
                <Button
                    variant="outlined"
                    startIcon={<GridOn />}
                    onClick={handleExportFullData}
                    sx={isRetro ? {
                        fontFamily: vortxTypography.mono,
                        fontSize: '0.75rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.1em',
                        borderRadius: 0,
                        borderColor: 'rgba(63, 106, 100, 0.4)',
                        color: retroColors.accent,
                        '&:hover': {
                            borderColor: retroColors.accent,
                            backgroundColor: 'rgba(95, 212, 196, 0.1)',
                        },
                    } : { textTransform: 'none' }}
                >
                    Full Data
                </Button>
                <Button
                    variant="outlined"
                    startIcon={<PictureAsPdf />}
                    onClick={handleExportPresentation}
                    sx={isRetro ? {
                        fontFamily: vortxTypography.mono,
                        fontSize: '0.75rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.1em',
                        borderRadius: 0,
                        borderColor: 'rgba(63, 106, 100, 0.4)',
                        color: retroColors.accent,
                        '&:hover': {
                            borderColor: retroColors.accent,
                            backgroundColor: 'rgba(95, 212, 196, 0.1)',
                        },
                    } : { textTransform: 'none' }}
                >
                    Presentation
                </Button>
                <Box sx={{ flex: 1 }} />
                <Button
                    onClick={onClose}
                    sx={isRetro ? {
                        fontFamily: vortxTypography.mono,
                        fontSize: '0.75rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.1em',
                        borderRadius: 0,
                        color: retroColors.muted,
                        '&:hover': {
                            backgroundColor: 'rgba(95, 212, 196, 0.1)',
                        },
                    } : { textTransform: 'none' }}
                >
                    Close
                </Button>
            </DialogActions>
        </Dialog>
    );
}
